import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { Download, Share, Check, Smartphone, Copy, QrCode } from "lucide-react";
import { toast } from "sonner";

export default function InstallPage() {
  const [deferred, setDeferred] = useState(null);
  const [canPrompt, setCanPrompt] = useState(false);
  const [installed, setInstalled] = useState(false);

  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  const isAndroid = /android/i.test(window.navigator.userAgent);
  const url = `${window.location.origin}/install`;

  useEffect(() => {
    const onBeforeInstall = (e) => { e.preventDefault(); setDeferred(e); setCanPrompt(true); };
    const onInstalled = () => { setInstalled(true); setCanPrompt(false); setDeferred(null); toast.success("PACEMIA installée 🎉"); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") toast.success("Installation lancée…");
      setDeferred(null); setCanPrompt(false);
    } else if (isIOS) {
      toast.info("Sur iPhone/iPad : touchez Partager puis « Sur l'écran d'accueil ».", { duration: 8000 });
    } else {
      toast.info("Ouvrez ce lien dans Chrome sur votre téléphone Android, puis touchez « Installer ».", { duration: 8000 });
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Lien copié"); }
    catch { toast.info(url); }
  };

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(url)}`;

  return (
    <div className="min-h-screen bg-[#0b3a82] text-white flex items-center justify-center p-5" data-testid="install-page">
      <Card className="w-full max-w-md p-7 rounded-2xl shadow-2xl bg-white text-foreground space-y-6">
        <div className="flex flex-col items-center text-center gap-3">
          <Logo size={40} />
          <div>
            <h1 className="font-display text-2xl font-bold">Installer l'application PACEMIA</h1>
            <p className="text-sm text-muted-foreground mt-1">Ajoutez PACEMIA à votre écran d'accueil et lancez-la comme une vraie app, même hors-ligne.</p>
          </div>
        </div>

        {isStandalone || installed ? (
          <div className="rounded-md bg-green-50 border border-green-200 p-4 text-center text-green-700 flex items-center justify-center gap-2" data-testid="install-done">
            <Check className="h-5 w-5" /> L'application est installée. Ouvrez-la depuis votre écran d'accueil.
          </div>
        ) : (
          <>
            <Button
              onClick={install}
              className="w-full h-14 text-base font-semibold gap-2 rounded-full"
              data-testid="install-page-btn"
            >
              <Download className="h-5 w-5" />
              {canPrompt ? "Installer maintenant" : isIOS ? "Comment installer (iPhone)" : "Installer l'application"}
            </Button>

            {isIOS && (
              <div className="rounded-md bg-secondary p-4 text-sm space-y-2" data-testid="install-ios-steps">
                <p className="font-medium flex items-center gap-2"><Share className="h-4 w-4" /> Sur iPhone / iPad :</p>
                <ol className="list-decimal list-inside text-muted-foreground space-y-1">
                  <li>Touchez le bouton <span className="font-medium text-foreground">Partager</span> de Safari.</li>
                  <li>Choisissez <span className="font-medium text-foreground">« Sur l'écran d'accueil »</span>.</li>
                  <li>Confirmez avec <span className="font-medium text-foreground">Ajouter</span>.</li>
                </ol>
              </div>
            )}

            {!isIOS && !canPrompt && (
              <div className="rounded-md bg-secondary p-4 text-sm text-muted-foreground flex items-start gap-2" data-testid="install-android-hint">
                <Smartphone className="h-4 w-4 mt-0.5 shrink-0" />
                Sur Android, ouvrez ce lien avec <span className="font-medium text-foreground">&nbsp;Chrome&nbsp;</span>, puis touchez « Installer ». Si rien ne se passe, utilisez le menu ⋮ → « Installer l'application ».
              </div>
            )}
          </>
        )}

        <div className="border-t border-border pt-5 text-center space-y-3">
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1"><QrCode className="h-3.5 w-3.5" /> Scannez ce QR code avec votre téléphone</p>
          <img src={qrSrc} alt="QR code installation PACEMIA" className="mx-auto rounded-lg border border-border" width={160} height={160} data-testid="install-qr" />
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-md bg-secondary px-3 py-2 text-xs">{url}</code>
            <Button variant="outline" size="icon" onClick={copyLink} data-testid="install-copy-link"><Copy className="h-4 w-4" /></Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
